import React, { useEffect, useRef } from "react";

/**
 * 3D-feeling background that reacts to scroll. Multiple tooth SVGs and floating dots
 * are translated + rotated on Y-axis proportional to scroll position for parallax depth.
 */
export default function AnimatedBackground() {
    const layerRefs = useRef([]);

    useEffect(() => {
        function onScroll() {
            const y = window.scrollY;
            layerRefs.current.forEach((el, i) => {
                if (!el) return;
                const depth = (i + 1) * 0.12;
                const rotate = y * 0.03 * (i % 2 === 0 ? 1 : -1);
                const translate = y * depth;
                el.style.transform = `translate3d(${Math.sin(y / 200) * 20}px, ${-translate}px, 0) rotateY(${rotate}deg) rotateX(${rotate * 0.6}deg)`;
            });
        }
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const Tooth = ({ size = 220, color = "#0F4C5C", opacity = 0.9 }) => (
        <svg viewBox="0 0 200 240" width={size} height={size}>
            <defs>
                <linearGradient id="tg" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity={opacity} />
                    <stop
                        offset="100%"
                        stopColor="#00A896"
                        stopOpacity={opacity * 0.6}
                    />
                </linearGradient>
            </defs>
            <path
                d="M100 15c-30 0-55 15-65 40-8 22-4 46 6 68 6 14 12 30 12 46 0 20 8 45 20 55 10 8 22-6 24-24 3-24 8-40 12-40s9 16 12 40c2 18 14 32 24 24 12-10 20-35 20-55 0-16 6-32 12-46 10-22 14-46 6-68-10-25-35-40-65-40-6 0-12 1-18 3-6-2-12-3-18-3z"
                fill="url(#tg)"
            />
        </svg>
    );

    return (
        <div
            className="absolute inset-0 overflow-hidden pointer-events-none"
            style={{ perspective: "1200px" }}
        >
            <div className="dk-grid-lines" />

            <div
                ref={(el) => (layerRefs.current[0] = el)}
                className="dk-tooth"
                style={{ top: "8%", left: "6%", width: 180, height: 200 }}
            >
                <Tooth size={200} color="#0F4C5C" opacity={0.35} />
            </div>
            <div
                ref={(el) => (layerRefs.current[1] = el)}
                className="dk-tooth"
                style={{ top: "18%", right: "10%", width: 260, height: 300 }}
            >
                <Tooth size={280} color="#00A896" opacity={0.28} />
            </div>
            <div
                ref={(el) => (layerRefs.current[2] = el)}
                className="dk-tooth"
                style={{ top: "58%", left: "12%", width: 140, height: 160 }}
            >
                <Tooth size={160} color="#0F4C5C" opacity={0.22} />
            </div>
            <div
                ref={(el) => (layerRefs.current[3] = el)}
                className="dk-tooth"
                style={{ top: "78%", right: "18%", width: 220, height: 240 }}
            >
                <Tooth size={240} color="#F4A261" opacity={0.22} />
            </div>
            <div
                ref={(el) => (layerRefs.current[4] = el)}
                className="dk-tooth"
                style={{ top: "115%", left: "40%", width: 300, height: 320 }}
            >
                <Tooth size={320} color="#00A896" opacity={0.22} />
            </div>

            {[...Array(12)].map((_, i) => (
                <div
                    key={i}
                    className="dk-floating-dot"
                    style={{
                        width: 8 + (i % 4) * 6,
                        height: 8 + (i % 4) * 6,
                        top: `${(i * 8.3) % 120}%`,
                        left: `${(i * 13.7) % 95}%`,
                        animationDelay: `${i * 0.6}s`,
                    }}
                />
            ))}
        </div>
    );
}
